import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BrowserCacheLocation,
  PublicClientApplication,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { environment } from '@siat-fe/env';
import { from, Observable, of, switchMap } from 'rxjs';
import {
  LoginRequest,
  LoginResponse,
  RegisterDTO,
  RegisterResponse,
} from '../models/login.model';
import { User } from '../models/userinfo.model';

export interface EntraLoginApiPayload {
  email: string;
  fiscalCode: string;
  idToken: string;
  registrationNumber: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  /**
   * api base url from the environment config.
   */
  private readonly baseUrl = environment.baseUrl;

  /**
   * lazily initialized msal client for entra login.
   */
  private entraClient: PublicClientApplication | null = null;

  /**
   * injected http client.
   */
  _httpClient = inject(HttpClient);

  /**
   * authenticates the user with codice fiscale, password and an optional activation pin.
   * @returns {Observable<LoginResponse>} the login response with tokens and user context.
   */
  login(loginDTO: LoginRequest): Observable<LoginResponse> {
    const loginApi = `${this.baseUrl}v1/login`;
    let httpParams = new HttpParams()
      .set('username', loginDTO.codiceFiscale)
      .set('password', loginDTO.password);

    if (loginDTO.pin) {
      httpParams = httpParams.set('pin', loginDTO.pin);
    }

    return this._httpClient.get<LoginResponse>(loginApi, {
      params: httpParams,
      headers: { 'skip-error-handling': 'true' },
    });
  }

  /**
   * generates and sends the activation code to the user's registered email.
   * @returns {Observable<string>} the raw text response from the api.
   */
  generateActivationCode(loginDTO: LoginRequest): Observable<string> {
    const generationCodeApi = `${this.baseUrl}v1/login/generation-code`;
    const httpParams = new HttpParams()
      .set('username', loginDTO.codiceFiscale)
      .set('password', loginDTO.password);

    return this._httpClient.get(generationCodeApi, {
      params: httpParams,
      headers: { 'skip-error-handling': 'true' },
      responseType: 'text',
    });
  }

  /**
   * completes an entra redirect login by resolving the pending msal promise and setting the active account.
   * @returns {Observable<AuthenticationResult | null>} the authentication result, or null if there was no pending redirect.
   */
  handleEntraRedirectResponse(): Observable<AuthenticationResult | null> {
    return from(this.getEntraClient()).pipe(
      switchMap((client) =>
        from(
          client.handleRedirectPromise({
            navigateToLoginRequestUrl: false,
          }),
        ),
      ),
      switchMap((authResult) => {
        if (authResult?.account) {
          this.entraClient?.setActiveAccount(authResult.account);
        }

        return of(authResult);
      }),
    );
  }

  /**
   * registers the user for firebase push notifications.
   * @returns {Observable<RegisterResponse>} the registration response.
   */
  registerFirebase(registerDTO: RegisterDTO): Observable<RegisterResponse> {
    const registerApi = `${this.baseUrl}v1/register`;
    return this._httpClient.post<RegisterResponse>(registerApi, registerDTO);
  }

  /**
   * silently refreshes the user token in the background, without the global loader or error snackbar.
   * @returns {Observable<LoginResponse>} the refreshed login response with new tokens.
   */
  refreshToken(): Observable<LoginResponse> {
    const refreshTokenApi = `${this.baseUrl}v1/user/refresh_token`;
    return this._httpClient.get<LoginResponse>(refreshTokenApi, {
      headers: { 'skip-error-handling': 'true', 'skip-loader': 'true' },
    });
  }

  /**
   * fetches the user's info; pass skipErrorHandling for silent background hydration.
   * @returns {Observable<User>} the user info.
   */
  getUserInfo(userId: string, skipErrorHandling = false): Observable<User> {
    const userInfoApi = `${this.baseUrl}v1/user/${userId}`;
    return this._httpClient.get<User>(
      userInfoApi,
      skipErrorHandling
        ? {
            headers: {
              'skip-error-handling': 'true',
              'skip-loader': 'true',
            },
          }
        : undefined,
    );
  }

  /**
   * logs out the user on the backend, silently and without the global loader.
   * @returns {Observable<unknown>} the logout response.
   */
  logout(): Observable<unknown> {
    const req = `${this.baseUrl}v1/auth/logout`;
    return this._httpClient.get(req, {
      headers: { 'skip-error-handling': 'true', 'skip-loader': 'true' },
    });
  }

  /**
   * starts the msal redirect flow toward the entra login page.
   */
  startEntraRedirectLogin(): Promise<void> {
    return this.getEntraClient().then((client) =>
      client.loginRedirect({
        scopes: environment.entra?.scopes?.length
          ? environment.entra.scopes
          : ['user.read'],
        prompt: 'select_account',
      }),
    );
  }

  /**
   * resolves the entra redirect and, if successful, logs the user in against the api.
   * @returns {Observable<LoginResponse | null>} the login response, or null if there was no pending redirect.
   */
  handleEntraRedirectLogin(): Observable<LoginResponse | null> {
    return this.handleEntraRedirectResponse().pipe(
      switchMap((authResult) => {
        if (!authResult) {
          return of(null);
        }

        const payload = this.buildEntraLoginApiPayload(authResult);
        return this.loginWithEntraApi(payload);
      }),
    );
  }

  /**
   * exchanges an entra identity payload for a siat session via the login-entra api.
   * @returns {Observable<LoginResponse>} the login response with tokens and user context.
   */
  loginWithEntraApi(payload: EntraLoginApiPayload): Observable<LoginResponse> {
    const loginApi =
      environment.entra?.loginApiUrl || `${this.baseUrl}v1/login-entra`;
    const httpParams = new HttpParams()
      .set('fiscalCode', payload.fiscalCode)
      .set('idToken', payload.idToken)
      .set('email', payload.email)
      .set('registrationNumber', payload.registrationNumber);

    return this._httpClient.post<LoginResponse>(
      loginApi,
      {},
      {
        params: httpParams,
        headers: { 'skip-error-handling': 'true' },
      },
    );
  }

  /**
   * extracts and validates the claims needed to call the login-entra api from an msal auth result.
   * @returns {EntraLoginApiPayload} the entra login api payload.
   */
  buildEntraLoginApiPayload(
    authResult: AuthenticationResult,
  ): EntraLoginApiPayload {
    const claims = authResult.idTokenClaims as
      | Record<string, unknown>
      | undefined;
    const fiscalCode = String(claims?.['CodiceFiscale'] || '');
    const email = String(claims?.['Mail'] || '');
    const registrationNumber = String(claims?.['Matricola'] || '');

    if (!fiscalCode) {
      throw new Error('Codice fiscale non disponibile nel token Entra.');
    }

    if (!email) {
      throw new Error('Email non disponibile nel token Entra.');
    }

    if (!registrationNumber) {
      throw new Error('Matricola non disponibile nel token Entra.');
    }

    if (!authResult.idToken) {
      throw new Error('idToken non disponibile nella risposta Entra.');
    }

    return {
      email,
      fiscalCode,
      idToken: authResult.idToken,
      registrationNumber,
    };
  }

  /**
   * lazily creates and initializes the msal client, reusing it across calls.
   * @returns {Promise<PublicClientApplication>} the initialized msal client.
   */
  private async getEntraClient(): Promise<PublicClientApplication> {
    const entraConfig = environment.entra;
    if (!entraConfig?.enabled) {
      throw new Error('Accesso Entra non configurato.');
    }

    if (!this.entraClient) {
      this.entraClient = new PublicClientApplication({
        auth: {
          clientId: entraConfig.clientId,
          authority: `https://login.microsoftonline.com/${entraConfig.tenantId}`,
          redirectUri: this.getEntraRedirectUri(),
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
        },
      });
      await this.entraClient.initialize();
    }

    return this.entraClient;
  }

  /**
   * resolves the msal redirect uri from the environment config, falling back to the entra-authenticator route.
   * @returns {string} the redirect uri.
   */
  private getEntraRedirectUri(): string {
    const configuredRedirectUri = environment.entra?.redirectUri?.trim();

    if (configuredRedirectUri) {
      return configuredRedirectUri;
    }

    return `${window.location.origin}/entra-authenticator/`;
  }
}
